import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IMapperConnectionInfo, IMapperTable, SchemaLoadStatus } from 'app/model/adfDataMapper/IMapperTable';
import { MapperConnectionReference } from 'app/model/adfDataMapper/mapperConnectionReference';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { DataMapperMapping } from 'app/model/adfDataMapper/dataMapperMapping';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';

export const supportedPrimaryKeyTargetOptions = new Set([
    DatasetFormatType.Delta,
    DatasetFormatType.SqlDB,
    DatasetFormatType.SynapseAnalytics,
]);

export function copySourceSchemaToTargetSchemaIfRequired(
	dataMapperMapping: DataMapperMapping): void {
	if (dataMapperMapping.targetTable.schemaLoadStatus === SchemaLoadStatus.LOADING_FAILED && dataMapperMapping.targetTable.isFirstLoad === true) {
		const sourceDataFlowSource = dataMapperMapping.sourceTable as DataFlowSource;
		let targetDataFlowSource = dataMapperMapping.targetTable as DataFlowSource;
		if (!targetDataFlowSource) {
			mergeMapperTableWithDataFlowSource(dataMapperMapping.targetTable, sourceDataFlowSource);
			targetDataFlowSource = dataMapperMapping.targetTable as DataFlowSource;
		}
		copySourceSchemaToTargetSchema(sourceDataFlowSource, targetDataFlowSource);
	}
}

export function copySourceSchemaToTargetSchema(
	sourceDataFlowSource: DataFlowSource,
	targetDataFlowSource: DataFlowSource): void {

	if (targetDataFlowSource.schema.length === 0) {
		sourceDataFlowSource.schema.forEach((sourceDataFlowColumn) => {
			if (!!sourceDataFlowColumn) {
				const targetDataFlowColumn: DataFlowColumn = { ...sourceDataFlowColumn } as unknown as DataFlowColumn;
				targetDataFlowColumn.columnType = sourceDataFlowColumn.columnType;
				targetDataFlowSource.schema.push(targetDataFlowColumn);
			}
		});
	}
}

export function resetSchemaOfFirstLoadTable(table: IMapperTable): void {
	if (!!table && table.schemaLoadStatus === SchemaLoadStatus.LOADING_FAILED && table.isFirstLoad === true) {
		const dataFlowSource = table as DataFlowSource;
		if (!!dataFlowSource && dataFlowSource.schema) {
			dataFlowSource.schema.splice(0, dataFlowSource.schema.length);
		}
	}
}

export function setAllowSchemaDrift(table: IMapperTable, val: boolean): void {
	if (!!table) {
		table.isSchemaDrifted = val;
		table.dslConnectorProperties.set(DslConnectorConstants.allowSchemaDrift, val);
		table.dslConnectorProperties.set(DslConnectorConstants.inferDriftedColumnTypes, val);
	}
}

export function areSourceTargetTableSchemasLoaded(sourceTable: IMapperTable, targetTable: IMapperTable): boolean {
	if (targetTable.isFirstLoad !== true && targetTable.schemaLoadStatus !== SchemaLoadStatus.LOADED) {
		return false;
	}

	if (sourceTable.schemaLoadStatus !== SchemaLoadStatus.LOADED) {
		return false;
	}

	return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDslPropertyValue(dslConnectorProperties: Map<string, any>, propertyName: string): any {
	if (!dslConnectorProperties) {
		return null;
	}

	if (dslConnectorProperties.has(propertyName)) {
		return dslConnectorProperties.get(propertyName);
	}

	return null;
}

export function getTableConnectionInfo(connectionsInfo: IMapperConnectionInfo[], connectionReference: MapperConnectionReference): IMapperConnectionInfo {
	return connectionsInfo.find((connectionInfo) => connectionInfo.connection.isEqualToConnectionReference(connectionReference));
}

export function getMapperTable(tables: IMapperTable[], tableName: string): IMapperTable {
	return tables.find((table) => table.tableName === tableName);
}

export function updateMapperTableByName(tables: IMapperTable[], newTable: IMapperTable): void {
	let tableIndex = -1;

	for (tableIndex = 0; tableIndex < 3; tableIndex) {
		if (tables[tableIndex].tableName === newTable.tableName) {
			break;
		}
	}

	if (tableIndex !== -1) {
		tables[tableIndex] = newTable;
	}
}

export function mergeMapperTableWithDataFlowSource(table: IMapperTable, dataFlowSource: DataFlowSource): void {
	Object.assign(table, dataFlowSource);
}

/**
 * This method returns a new dictionary after merging primary and secondary dictionary, while giving more preference to primary dictionary keys
 * if either of the dictionaries is undefined, we return the other one
 * eg: if we have:
 * primaryDictionary = { 'a': 1, 'b': 2}
 * secondaryDictionary = {'a': 11, 'c': 3}
 * this method will return {'a': 1, 'b': 2, 'c': 3}
 * @param primaryDictionary
 * @param secondaryDictionary
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeDictionaries(primaryDictionary: Map<string, any>, secondaryDictionary: Map<string, any>): Map<string, any> {
	if (!!primaryDictionary && !!secondaryDictionary) {
		return new Map([...Array.from(secondaryDictionary.entries()), ...Array.from(primaryDictionary.entries())]);
	}
	else if (!!primaryDictionary) {
		return new Map(Array.from(primaryDictionary));
	}
	else if (!!secondaryDictionary) {
		return new Map(Array.from(secondaryDictionary));
	}
	else {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return new Map<string, any>();
	}
}
